import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  useGallery,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface HeroSplitBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  altText?: string;
  /**
   * Property field (or collection-indexed path) used as the default hero
   * background image, so each interior page can default to an image that
   * relates to its own content instead of the generic featured shot.
   * Examples: `property_featured_image` (default), `amenities[0].amenity_image`,
   * `floorplans[0].floorplan_image_url`. Editors can still override per page.
   */
  bgImageField?: string;
  /**
   * Literal default hero background image URL. When set, it takes precedence
   * over `bgImageField` so a page can default to a specific image asset (rather
   * than a property data field). Editors can still override it per page.
   */
  bgImageUrl?: string;
  /**
   * Optional field (or collection-indexed path) for the default alt text that
   * accompanies `bgImageField` (e.g. `amenities[0].amenity_image_alt_text`).
   */
  bgAltField?: string;
}

// Default background image for the hero. Defaults to the property's featured
// image and falls back to the first photo-gallery shot, so any page that
// reuses this hero without overriding `bgImageField` still gets a relevant
// image with zero per-page configuration.
const BG_IMAGE_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hero background' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function HeroSplitBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
  altText = 'Hero background',
  bgImageField,
  bgImageUrl,
  bgAltField,
}: HeroSplitBGProps) {
  const prefix = tokenPrefix || 'herosplitbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const bgPath = `${prefix}.bg_image`;

  // The default URL tracks the default CTA label so the link target matches
  // the verb ("View Floor Plans" → /floor-plans). Each page in pages.json
  // is expected to pass `ctaLabel` and `ctaUrl` together when overriding;
  // property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  // Background image definition, built per-page from props. A page can pass a
  // literal `bgImageUrl` to default the hero to a specific image asset, or
  // `bgImageField` (e.g. `amenities[0].amenity_image` on the Amenities page) to
  // default to a content-relevant property field; otherwise it defaults to the
  // property's featured image. If the chosen default resolves empty, the
  // component-level fallback chain below (featured image → first gallery photo)
  // takes over — `sourceBinding` only governs editor source-swaps, not the
  // runtime default, so it is not relied on for that fallback.
  const bgImageDef: SlotGroupDefinition = {
    label: 'Background Image',
    fields: {
      image: bgImageUrl
        ? {
            label: 'Image',
            type: 'image',
            defaultValue: bgImageUrl,
          }
        : {
            label: 'Image',
            type: 'image',
            defaultField: bgImageField || 'property_featured_image',
          },
      alt: bgAltField
        ? { label: 'Alt Text', type: 'text', defaultField: bgAltField }
        : { label: 'Alt Text', type: 'text', defaultValue: altText },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: 0,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  // Editable hero image. `useSlotGroup` resolves the slot group's
  // `defaultField` (the page-relevant default below), or whatever the editor
  // selects once they override it.
  const bgImage = useSlotGroup(bgPath, bgImageDef);

  // Graceful, error-aware fallback chain. Two failure modes have to be
  // handled so the hero never lands on a blank/gray box:
  //
  //  1. Empty source. `useSlotGroup` only resolves the single `defaultField`
  //     path — `sourceBinding.defaultIndex` is NOT an automatic runtime
  //     fallback (it only applies when an editor swaps the source). So a
  //     page-relevant default like `floorplans[0].floorplan_image_url` returns
  //     null whenever that collection image is missing.
  //  2. Broken source. A non-empty but unloadable URL (404, a non-image asset
  //     like a floor-plan PDF, a host that blocks hotlinking) makes
  //     `VibesitesImage` render its own gray placeholder.
  //
  // We build an ordered candidate list — page-relevant default → property
  // featured image → first usable gallery photo — and advance to the next
  // candidate whenever the current one fails to load. Gallery photos
  // frequently ship with an empty `image` and only an `original_url`, so check
  // both and scan past blank entries instead of trusting index 0.
  const featuredImage = usePropertyField('property_featured_image');
  const gallery = useGallery() as Array<{ image?: string | null; original_url?: string | null }>;
  const galleryFallback = useMemo(() => {
    for (const photo of gallery) {
      const src = photo?.image || photo?.original_url;
      if (src) return src;
    }
    return null;
  }, [gallery]);

  const candidates = useMemo(
    () =>
      [bgImage.image, featuredImage, galleryFallback].filter(
        (url): url is string => Boolean(url),
      ),
    [bgImage.image, featuredImage, galleryFallback],
  );
  const candidatesKey = candidates.join('|');
  const [candidateIdx, setCandidateIdx] = useState(0);
  // Reset to the most-preferred candidate whenever the list itself changes.
  useEffect(() => {
    setCandidateIdx(0);
  }, [candidatesKey]);
  const displayImage = candidates[candidateIdx] || null;

  // Stable alt text fallback for the background image.
  const imageAlt = useMemo(() => bgImage.alt || altText, [bgImage.alt, altText]);

  return (
    <section
      className="relative flex items-center overflow-hidden text-white min-h-[60vh] min-h-[480px] pb-14 md:pb-20"
      style={{
        paddingTop: 'calc(var(--noh-banner-height, 0px) + var(--offer-banner-height, 0px) + 5rem)',
        transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Full-bleed background image covering the entire section + dark
          overlay. A plain `absolute inset-0` layer — no `position: fixed` and
          no scroll listeners, so there is no parallax: the image scrolls with
          the page like any other content. */}
      <EditableSlotGroup
        groupPath={bgPath}
        definition={bgImageDef}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            // `key` forces a fresh element per candidate so the browser
            // re-attempts loading when we advance past a broken URL.
            key={displayImage}
            url={displayImage}
            alt={imageAlt}
            priority
            className="absolute inset-0 h-full w-full object-cover"
            onError={() =>
              setCandidateIdx((i) => (i < candidates.length - 1 ? i + 1 : i))
            }
          />
        ) : (
          <div className="absolute inset-0 bg-primary" />
        )}
        <div className="absolute inset-0 bg-black/40" />
      </EditableSlotGroup>

      {/* Text content, pushed to the right side of the section. */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
        <div className="hero-split-content flex flex-col gap-6 items-start w-full max-w-xl">

          {/* Page Title */}
          <div className="min-w-0 w-full">
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-wider leading-tight break-words text-left drop-shadow-lg"
            />
          </div>

          {/* Small white divider beneath the title */}
          <div
            aria-hidden="true"
            className="h-0.5 w-12 bg-white/80 drop-shadow"
          />

          {/* Blurb + CTA */}
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="div"
            className="font-body text-base md:text-lg leading-relaxed opacity-95 drop-shadow [&_p]:mb-5 [&_p:last-child]:mb-0 [&_a]:underline text-left"
          />
          <div className="text-left">
            <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
              <a
                href={cta.url || '#'}
                target={cta._target || '_self'}
                className="hero-split-cta-btn group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{cta.label || ctaLabel}</span>
              </a>
            </EditableSlotGroup>
          </div>

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosplitbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosplitbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosplitbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosplitbg.bg_image"
        definition={BG_IMAGE_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Content fade-in on first paint. The hero text/CTA ease up into place
           once, on load. Honors prefers-reduced-motion. */
        .hero-split-content {
          animation: heroSplitFadeIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes heroSplitFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-split-content { animation: none; }
        }

        /* CTA button — outlined white on top of the brand panel. */
        .hero-split-cta-btn {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, #fff 75%, transparent);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-split-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, #fff 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .hero-split-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.25),
            0 4px 16px color-mix(in srgb, #fff 18%, transparent);
        }
        .hero-split-cta-btn:hover::before {
          opacity: 1;
        }
        .hero-split-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSplitBG',
  displayName: 'Hero – Split Background',
  tokens: {
    'herosplitbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosplitbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosplitbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'herosplitbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hero background' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(HeroSplitBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSplitBG;
